import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createChart, CandlestickSeries, HistogramSeries } from 'lightweight-charts';
import type { IChartApi, ISeriesApi, Time, CandlestickSeriesPartialOptions, IPriceLine, HistogramData, IPaneApi } from 'lightweight-charts';
import { tdSequentialService } from '../services/tdSequentialService';
import { ninerBreadthService } from '../services/ninerBreadthService';
import { profileService } from '../services/profileService';
import type { TDSequentialSettings, OHLCBar } from '../types/tdSequential';
import { DEFAULT_TD_SETTINGS } from '../types/tdSequential';
import type { FilterType, NinerBreadthFilters, ActiveFilter } from '../types/ninerBreadth';

interface TDSequentialWidgetProps {
  windowId: string;
  onRemove: () => void;
}

interface TDSequentialWidgetState {
  symbol: string;
  daysBack: number;
  settings: TDSequentialSettings;
  zscoreFilter: ActiveFilter;
  showZScore: boolean;
}

interface TDMarker {
  time: string;
  value: number;
  text: string;
  color: string;
  position: 'above' | 'below';
  isPerfected?: boolean;
  isQualified?: boolean;
}

const DEFAULT_TICKERS = 'SPX,SPY,QQQ,DIA,IWM';

const FILTER_LABELS: Record<FilterType, string> = {
  tickers: 'Tickers',
  asset_type: 'Asset Type',
  exchange: 'Exchange',
  sector: 'Sector',
  sub_sector: 'Sub-Sector',
};

// Colors
const COLORS = {
  background: '#0A0A0A',
  gridLines: '#161616',
  text: '#9ca3af',
  buySetup: '#1C76A2',
  sellSetup: '#D8483B',
  countdown: '#C2A14E',
  tdstSupport: '#1C76A2',
  tdstResistance: '#D8483B',
  perfected: '#22d3ee',
  candleUp: '#1C76A2',
  candleDown: '#D8483B',
  zscorePositive: '#D8483B',
  zscoreNegative: '#1C76A2',
  zscoreNeutral: '#C2A14E',
};

export function TDSequentialWidget({ windowId, onRemove }: TDSequentialWidgetProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const zscorePaneRef = useRef<IPaneApi<Time> | null>(null);
  const zscoreSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const priceLinesRef = useRef<IPriceLine[]>([]);
  const markersRef = useRef<TDMarker[]>([]);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadedRef = useRef(false);
  const isDisposedRef = useRef(false);

  const [symbol, setSymbol] = useState('SPY');
  const [symbolInput, setSymbolInput] = useState('SPY');
  const [daysBack, setDaysBack] = useState(180);
  const [daysBackInput, setDaysBackInput] = useState('180');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<TDSequentialSettings>(DEFAULT_TD_SETTINGS);
  const [visibleMarkers, setVisibleMarkers] = useState<Array<TDMarker & { x: number; y: number }>>([]);

  // Z-Score oscillator state
  const [showZScore, setShowZScore] = useState(true);
  const [zscoreFilter, setZscoreFilter] = useState<ActiveFilter>({
    type: 'tickers',
    value: DEFAULT_TICKERS,
  });
  const [tickerInput, setTickerInput] = useState(DEFAULT_TICKERS);

  // Load state from backend on mount
  useEffect(() => {
    const loadState = async () => {
      try {
        const response = await profileService.getFrontendState();
        const savedState = response.frontend_state?.tdSequentialWidgets?.[windowId] as TDSequentialWidgetState | undefined;

        if (savedState) {
          setSymbol(savedState.symbol);
          setSymbolInput(savedState.symbol);
          setDaysBack(savedState.daysBack);
          setDaysBackInput(savedState.daysBack.toString());
          setSettings(savedState.settings);
          if (savedState.zscoreFilter) {
            setZscoreFilter(savedState.zscoreFilter);
            if (savedState.zscoreFilter.type === 'tickers') {
              setTickerInput(savedState.zscoreFilter.value);
            }
          }
          if (savedState.showZScore !== undefined) {
            setShowZScore(savedState.showZScore);
          }
        }
        isLoadedRef.current = true;
      } catch (error) {
        console.error('Failed to load TD Sequential state:', error);
        isLoadedRef.current = true;
      }
    };
    loadState();
  }, [windowId]);

  // Save state to backend (debounced)
  useEffect(() => {
    if (!isLoadedRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        // Backend uses deep merge - only send this widget's state
        await profileService.saveFrontendState({
          tdSequentialWidgets: {
            [windowId]: {
              symbol,
              daysBack,
              settings,
              zscoreFilter,
              showZScore,
            },
          },
        });
      } catch (error) {
        console.error('Failed to save TD Sequential state:', error);
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [windowId, symbol, daysBack, settings, zscoreFilter, showZScore]);

  // Fetch OHLC data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ohlcTDSequential', symbol, daysBack],
    queryFn: () => tdSequentialService.fetchOHLC(symbol, daysBack),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Fetch filter options
  const { data: filters } = useQuery({
    queryKey: ['ninerBreadthFilters'],
    queryFn: () => ninerBreadthService.fetchFilters(),
    staleTime: 60 * 60 * 1000,
  });

  // Fetch z-score series - uses same daysBack as OHLC
  const { data: zscoreData, refetch: refetchZScore } = useQuery({
    queryKey: ['zscoreSeries', zscoreFilter.type, zscoreFilter.value, daysBack],
    queryFn: () => ninerBreadthService.fetchZScoreSeries(zscoreFilter.type, zscoreFilter.value, daysBack, 252),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: showZScore,
  });

  const handleSymbolSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (symbolInput.trim()) {
      setSymbol(symbolInput.trim().toUpperCase());
    }
  };

  const handleDaysBackChange = (value: string) => {
    setDaysBackInput(value);
    const num = parseInt(value, 10);
    if (!isNaN(num) && num > 0 && num <= 365) {
      setDaysBack(num);
    }
  };

  const toggleSetting = (key: keyof TDSequentialSettings) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Filter handlers
  const handleFilterTypeChange = (newType: FilterType) => {
    if (newType === 'tickers') {
      setZscoreFilter({ type: 'tickers', value: tickerInput || DEFAULT_TICKERS });
    } else {
      const options = getFilterOptions(newType, filters);
      setZscoreFilter({ type: newType, value: options[0] || '' });
    }
  };

  const handleFilterValueChange = (value: string) => {
    if (zscoreFilter.type === 'tickers') {
      setTickerInput(value);
    }
    setZscoreFilter((prev) => ({ ...prev, value }));
  };

  const handleTickerSubmit = () => {
    if (tickerInput.trim()) {
      setZscoreFilter({ type: 'tickers', value: tickerInput.trim().toUpperCase() });
    }
  };

  const getFilterOptions = (type: FilterType, filterData?: NinerBreadthFilters): string[] => {
    if (!filterData) return [];
    switch (type) {
      case 'asset_type':
        return filterData.asset_types;
      case 'exchange':
        return filterData.exchanges;
      case 'sector':
        return filterData.sectors;
      case 'sub_sector':
        return filterData.sub_sectors;
      default:
        return [];
    }
  };

  // Compute TD markers from OHLC data
  const computeMarkers = useCallback((bars: OHLCBar[], currentSettings: TDSequentialSettings): TDMarker[] => {
    const newMarkers: TDMarker[] = [];

    bars.forEach((bar) => {
      const td = bar.td_sequential;
      if (!td) return;

      const time = bar.timestamp.split('T')[0];

      if (currentSettings.showSetupCounts) {
        const count = td.setup_count;
        const isComplete = td.setup_complete;
        const isPerfected = td.setup_perfected;

        if (count >= 8) {
          let displayText = count.toString();
          if (isPerfected && currentSettings.showPerfectedHighlights) {
            displayText = '★';
          } else if (isComplete) {
            displayText = '●';
          }

          newMarkers.push({
            time,
            value: bar.high,
            text: displayText,
            color: COLORS.countdown,
            position: 'above',
            isPerfected: isPerfected && currentSettings.showPerfectedHighlights,
          });
        } else if (count <= -8) {
          let displayText = count.toString();
          if (isPerfected && currentSettings.showPerfectedHighlights) {
            displayText = '★';
          } else if (isComplete) {
            displayText = '●';
          }

          newMarkers.push({
            time,
            value: bar.low,
            text: displayText,
            color: COLORS.countdown,
            position: 'below',
            isPerfected: isPerfected && currentSettings.showPerfectedHighlights,
          });
        }
      }

      if (currentSettings.showCountdownCounts && td.countdown_complete) {
        const isBuyCountdown = td.setup_direction === 'buy';
        const isQualified = td.countdown_qualified;

        newMarkers.push({
          time,
          value: isBuyCountdown ? bar.low : bar.high,
          text: '★',
          color: COLORS.countdown,
          position: isBuyCountdown ? 'below' : 'above',
          isQualified: isQualified && currentSettings.showQualifiedHighlights,
        });
      }
    });

    return newMarkers;
  }, []);

  // Update visible markers based on chart coordinates
  const updateVisibleMarkers = useCallback(() => {
    if (isDisposedRef.current) return;
    if (!chartRef.current || !candlestickSeriesRef.current || markersRef.current.length === 0) {
      setVisibleMarkers([]);
      return;
    }

    try {
      const chart = chartRef.current;
      const series = candlestickSeriesRef.current;
      const timeScale = chart.timeScale();

      const newVisibleMarkers: Array<TDMarker & { x: number; y: number }> = [];

      markersRef.current.forEach((marker) => {
        const x = timeScale.timeToCoordinate(marker.time as Time);
        const y = series.priceToCoordinate(marker.value);

        if (x !== null && y !== null) {
          newVisibleMarkers.push({
            ...marker,
            x,
            y: marker.position === 'above' ? y - 20 : y + 12,
          });
        }
      });

      setVisibleMarkers(newVisibleMarkers);
    } catch {
      // Chart may be disposed
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isDisposedRef.current = true;
    };
  }, []);

  // Initialize main chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    isDisposedRef.current = false;

    const chart = createChart(chartContainerRef.current, {
      autoSize: true,
      layout: {
        background: { color: COLORS.background },
        textColor: COLORS.text,
      },
      grid: {
        vertLines: { color: COLORS.gridLines },
        horzLines: { color: COLORS.gridLines },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: COLORS.gridLines,
      },
      timeScale: {
        borderColor: COLORS.gridLines,
        timeVisible: true,
        secondsVisible: false,
      },
    });

    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: COLORS.candleUp,
      downColor: COLORS.candleDown,
      borderUpColor: COLORS.candleUp,
      borderDownColor: COLORS.candleDown,
      wickUpColor: COLORS.candleUp,
      wickDownColor: COLORS.candleDown,
    } as CandlestickSeriesPartialOptions);

    chartRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;

    // autoSize: true handles resize automatically

    chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
      if (!isDisposedRef.current) {
        requestAnimationFrame(updateVisibleMarkers);
      }
    });

    chart.subscribeCrosshairMove(() => {
      if (!isDisposedRef.current) {
        requestAnimationFrame(updateVisibleMarkers);
      }
    });

    return () => {
      chart.remove();
      chartRef.current = null;
      candlestickSeriesRef.current = null;
      zscorePaneRef.current = null;
      zscoreSeriesRef.current = null;
      priceLinesRef.current = [];
    };
  }, [updateVisibleMarkers]);

  // Create/remove z-score pane based on showZScore
  useEffect(() => {
    if (isDisposedRef.current || !chartRef.current) return;

    const chart = chartRef.current;

    if (showZScore && !zscorePaneRef.current) {
      // Create z-score pane
      try {
        const pane = chart.addPane();
        pane.setStretchFactor(0.25); // Z-score pane is 25% of total height

        const zscoreSeries = pane.addSeries(HistogramSeries, {
          color: COLORS.zscoreNeutral,
          priceFormat: {
            type: 'price',
            precision: 2,
            minMove: 0.01,
          },
        });

        // Add zero line
        zscoreSeries.createPriceLine({
          price: 0,
          color: '#4b5563',
          lineWidth: 1,
          lineStyle: 0,
          axisLabelVisible: false,
        });

        zscorePaneRef.current = pane;
        zscoreSeriesRef.current = zscoreSeries;

        // Load data if available
        if (zscoreData?.series) {
          const histogramData: HistogramData<Time>[] = zscoreData.series.map((point) => ({
            time: point.market_date as Time,
            value: point.zscore,
            color: point.zscore > 0.5 ? COLORS.zscorePositive : point.zscore < -0.5 ? COLORS.zscoreNegative : COLORS.zscoreNeutral,
          }));
          zscoreSeries.setData(histogramData);
        }
      } catch (err) {
        console.error('Failed to create z-score pane:', err);
      }
    } else if (!showZScore && zscorePaneRef.current) {
      // Remove z-score pane
      try {
        const panes = chart.panes();
        const paneIndex = panes.indexOf(zscorePaneRef.current);
        if (paneIndex > 0) {
          chart.removePane(paneIndex);
        }
        zscorePaneRef.current = null;
        zscoreSeriesRef.current = null;
      } catch (err) {
        console.error('Failed to remove z-score pane:', err);
      }
    }
  }, [showZScore, zscoreData]);

  // Update main chart data
  useEffect(() => {
    if (isDisposedRef.current) return;
    if (!candlestickSeriesRef.current || !data?.data?.ohlc) {
      return;
    }

    const bars = data.data.ohlc;

    const candleData = bars.map((bar) => ({
      time: bar.timestamp.split('T')[0] as Time,
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
    }));

    try {
      candlestickSeriesRef.current.setData(candleData);

      const newMarkers = computeMarkers(bars, settings);
      markersRef.current = newMarkers;

      if (chartRef.current) {
        chartRef.current.timeScale().fitContent();
      }

      setTimeout(() => updateVisibleMarkers(), 50);
    } catch {
      // Chart may be disposed
    }
  }, [data, settings, computeMarkers, updateVisibleMarkers]);

  // Update z-score pane data
  useEffect(() => {
    if (isDisposedRef.current) return;
    if (!zscoreSeriesRef.current || !zscoreData?.series || !showZScore) {
      return;
    }

    const histogramData: HistogramData<Time>[] = zscoreData.series.map((point) => ({
      time: point.market_date as Time,
      value: point.zscore,
      color: point.zscore > 0.5 ? COLORS.zscorePositive : point.zscore < -0.5 ? COLORS.zscoreNegative : COLORS.zscoreNeutral,
    }));

    try {
      zscoreSeriesRef.current.setData(histogramData);
    } catch {
      // Chart may be disposed
    }
  }, [zscoreData, showZScore]);

  // Update TDST price lines
  useEffect(() => {
    if (isDisposedRef.current) return;
    if (!candlestickSeriesRef.current || !data?.data?.ohlc) return;

    const series = candlestickSeriesRef.current;

    // Remove existing price lines
    priceLinesRef.current.forEach((line) => {
      try {
        series.removePriceLine(line);
      } catch {
        // Line may have already been removed
      }
    });
    priceLinesRef.current = [];

    const bars = data.data.ohlc;

    let latestSupport: number | null = null;
    let latestResistance: number | null = null;

    for (let i = bars.length - 1; i >= 0; i--) {
      const td = bars[i].td_sequential;
      if (!td) continue;

      if (latestSupport === null && td.tdst_support !== null) {
        latestSupport = td.tdst_support;
      }
      if (latestResistance === null && td.tdst_resistance !== null) {
        latestResistance = td.tdst_resistance;
      }
      if (latestSupport !== null && latestResistance !== null) break;
    }

    try {
      if (settings.showTDSTSupport && latestSupport !== null) {
        const line = series.createPriceLine({
          price: latestSupport,
          color: COLORS.tdstSupport,
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: 'TDST-S',
        });
        priceLinesRef.current.push(line);
      }

      if (settings.showTDSTResistance && latestResistance !== null) {
        const line = series.createPriceLine({
          price: latestResistance,
          color: COLORS.tdstResistance,
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: 'TDST-R',
        });
        priceLinesRef.current.push(line);
      }
    } catch {
      // Chart may be disposed
    }
  }, [data, settings.showTDSTSupport, settings.showTDSTResistance]);

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle only */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
          Niner Chart {symbol && <>— <span className="text-accent-yellow">{symbol}</span></>}
        </h3>
      </div>

      {/* Toolbar - NOT draggable */}
      <div className="px-3 py-1.5 border-b border-dark-border flex items-center gap-2 bg-dark-bg/50 flex-wrap relative z-20">
        <form onSubmit={handleSymbolSubmit} className="flex items-center gap-1">
          <input
            type="text"
            value={symbolInput}
            onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
            placeholder="Symbol"
            className="w-16 px-2 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
          />
          <button
            type="submit"
            className="px-2 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
          >
            Go
          </button>
        </form>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">Days:</span>
          <input
            type="number"
            value={daysBackInput}
            onChange={(e) => handleDaysBackChange(e.target.value)}
            min="1"
            max="365"
            className="w-14 px-2 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white focus:outline-none focus:border-accent-yellow"
          />
        </div>

        {/* Z-Score Toggle */}
        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={showZScore}
            onChange={(e) => setShowZScore(e.target.checked)}
            className="rounded border-dark-border bg-dark-surface text-bull-blue focus:ring-accent-yellow w-3 h-3"
          />
          <span className="text-xs text-gray-400">Z-Score</span>
        </label>

        <button
          onClick={() => { refetch(); refetchZScore(); }}
          className="px-2 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
          title="Refresh"
        >
          ↻
        </button>

        <div className="flex-1" />

        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`text-gray-400 hover:text-white text-sm px-2 py-0.5 rounded transition-colors ${
            showSettings ? 'bg-dark-border text-white' : ''
          }`}
          title="Settings"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Z-Score Filter Bar */}
      {showZScore && (
        <div className="flex items-center gap-2 px-3 py-1 bg-dark-bg/30 border-b border-dark-border relative z-20">
          <span className="text-[10px] text-gray-500 uppercase">Z-Score Filter:</span>
          <select
            value={zscoreFilter.type}
            onChange={(e) => handleFilterTypeChange(e.target.value as FilterType)}
            className="text-xs bg-dark-surface border border-dark-border rounded px-1.5 py-0.5 text-gray-300 focus:outline-none focus:border-accent-yellow"
          >
            {Object.entries(FILTER_LABELS).map(([type, label]) => (
              <option key={type} value={type}>
                {label}
              </option>
            ))}
          </select>

          {zscoreFilter.type === 'tickers' ? (
            <div className="flex items-center gap-1 flex-1">
              <input
                type="text"
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleTickerSubmit()}
                placeholder="SPX,SPY,QQQ"
                className="flex-1 min-w-0 text-xs bg-dark-surface border border-dark-border rounded px-1.5 py-0.5 text-gray-300 placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
              />
              <button
                onClick={handleTickerSubmit}
                className="text-xs px-2 py-0.5 bg-dark-surface-light border border-dark-border rounded text-gray-300 hover:bg-dark-border transition-colors"
              >
                Apply
              </button>
            </div>
          ) : (
            <select
              value={zscoreFilter.value}
              onChange={(e) => handleFilterValueChange(e.target.value)}
              className="flex-1 min-w-0 text-xs bg-dark-surface border border-dark-border rounded px-1.5 py-0.5 text-gray-300 focus:outline-none focus:border-accent-yellow"
            >
              {getFilterOptions(zscoreFilter.type, filters).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="px-3 py-2 border-b border-dark-border bg-dark-bg/50 relative z-20">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showSetupCounts}
                onChange={() => toggleSetting('showSetupCounts')}
                className="rounded border-dark-border bg-dark-surface text-bull-blue focus:ring-accent-yellow"
              />
              <span className="text-gray-300">Setup Counts (1-9)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showCountdownCounts}
                onChange={() => toggleSetting('showCountdownCounts')}
                className="rounded border-dark-border bg-dark-surface text-bull-blue focus:ring-accent-yellow"
              />
              <span className="text-gray-300">Countdown (1-13)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showTDSTSupport}
                onChange={() => toggleSetting('showTDSTSupport')}
                className="rounded border-dark-border bg-dark-surface text-bull-blue focus:ring-accent-yellow"
              />
              <span className="text-gray-300">TDST Support</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showTDSTResistance}
                onChange={() => toggleSetting('showTDSTResistance')}
                className="rounded border-dark-border bg-dark-surface text-bull-blue focus:ring-accent-yellow"
              />
              <span className="text-gray-300">TDST Resistance</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showPerfectedHighlights}
                onChange={() => toggleSetting('showPerfectedHighlights')}
                className="rounded border-dark-border bg-dark-surface text-bull-blue focus:ring-accent-yellow"
              />
              <span className="text-gray-300">Perfected 9s</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showQualifiedHighlights}
                onChange={() => toggleSetting('showQualifiedHighlights')}
                className="rounded border-dark-border bg-dark-surface text-bull-blue focus:ring-accent-yellow"
              />
              <span className="text-gray-300">Qualified 13s</span>
            </label>
          </div>
        </div>
      )}

      {/* Chart Container - single chart with panes */}
      <div className="relative flex-1 overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark-surface/80 z-10">
            <span className="text-gray-400 text-sm">Loading...</span>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark-surface/80 z-10">
            <span className="text-bear-red text-sm">Error loading data</span>
          </div>
        )}

        <div ref={chartContainerRef} className="w-full h-full" />

        {/* TD Markers Overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
          {visibleMarkers.map((marker, index) => (
            <div
              key={`${marker.time}-${marker.text}-${index}`}
              style={{
                position: 'absolute',
                left: `${marker.x}px`,
                top: `${marker.y}px`,
                color: marker.color,
                fontSize: '13px',
                fontWeight: 'bold',
                transform: 'translateX(-50%)',
                textShadow: '0 1px 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.8)',
              }}
            >
              {marker.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
